import React, { useState } from 'react';
import { 
  Crown, 
  Star, 
  Zap, 
  Heart, 
  Shield, 
  Trophy, 
  Gem, 
  Sparkles, 
  Lock,
  CheckCircle,
  Clock,
  Target,
  Award,
  Coins,
  Users,
  Calendar,
  TrendingUp,
  BookOpen,
  MessageCircle,
  Video,
  Mic,
  Camera,
  X
} from 'lucide-react';
import { AvatarWithBadges, USER_BADGES, UserBadge } from './AvatarWithBadges';

export interface BadgeProgress {
  badgeId: string;
  current: number;
  target: number;
  unit: string;
  description: string;
}

export interface BadgeManagerProps {
  user?: any;
  onClose?: () => void;
}

export const BadgeManager: React.FC<BadgeManagerProps> = ({ user, onClose }) => {
  const [activeTab, setActiveTab] = useState<'earned' | 'available' | 'progress'>('earned');

  // Get user's earned badges
  const getEarnedBadges = (): UserBadge[] => {
    if (!user) return [];

    const earned: UserBadge[] = [];

    // Role-based badges
    if (user.role === 'super_admin') earned.push(USER_BADGES['super-admin']);
    if (user.role === 'healthcare_provider') earned.push(USER_BADGES['healthcare-provider']);
    if (user.role === 'guru') earned.push(USER_BADGES['guru']);
    if (user.role === 'lab_technician') earned.push(USER_BADGES['lab-technician']);

    // Achievement badges
    if (user.is_early_adopter) earned.push(USER_BADGES['early-adopter']);
    if (user.is_supporter) earned.push(USER_BADGES['supporter']);
    if (user.trust_score >= 800) earned.push(USER_BADGES['trusted']);

    return earned.sort((a, b) => b.priority - a.priority);
  };

  // Get available badges (not yet earned)
  const getAvailableBadges = (): UserBadge[] => {
    const earned = getEarnedBadges();
    const earnedIds = earned.map(b => b.id);
    
    return Object.values(USER_BADGES)
      .filter(badge => !earnedIds.includes(badge.id))
      .sort((a, b) => b.priority - a.priority);
  };

  // Get badge progress
  const getBadgeProgress = (): BadgeProgress[] => {
    if (!user) return [];

    return [
      {
        badgeId: 'trusted',
        current: user.trust_score || 0,
        target: 800,
        unit: 'points',
        description: 'Reach a trust score of 800+'
      },
      {
        badgeId: 'supporter',
        current: user.subscription_months || 0,
        target: 1,
        unit: 'months',
        description: 'Become a premium supporter'
      },
      {
        badgeId: 'guru',
        current: user.sessions_hosted || 0,
        target: 10,
        unit: 'sessions',
        description: 'Host 10+ successful sessions'
      }
    ];
  };

  const earnedBadges = getEarnedBadges();
  const availableBadges = getAvailableBadges();
  const badgeProgress = getBadgeProgress();

  const tabs = [
    { id: 'earned', label: 'Earned', count: earnedBadges.length },
    { id: 'available', label: 'Available', count: availableBadges.length },
    { id: 'progress', label: 'Progress', count: badgeProgress.length }
  ];

  const renderBadgeCard = (badge: UserBadge, isEarned: boolean = true) => {
    const Icon = badge.icon;
    const progress = badgeProgress.find(p => p.badgeId === badge.id);
    const progressPercentage = progress ? (progress.current / progress.target) * 100 : 0;

    return (
      <div className={`bg-white rounded-lg border p-4 ${isEarned ? 'border-green-200' : 'border-gray-200'}`}>
        <div className="flex items-start space-x-3">
          <div className={`w-12 h-12 rounded-full ${badge.bgColor} flex items-center justify-center`}>
            <Icon className={`w-6 h-6 ${badge.color}`} />
          </div>
          <div className="flex-1">
            <div className="flex items-center space-x-2">
              <h3 className="font-semibold text-gray-900">{badge.label}</h3>
              {isEarned && <CheckCircle className="w-4 h-4 text-green-500" />}
            </div>
            <p className="text-sm text-gray-600 mt-1">{badge.description}</p>
            
            {progress && !isEarned && (
              <div className="mt-3">
                <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                  <span>Progress</span>
                  <span>{progress.current}/{progress.target} {progress.unit}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${Math.min(progressPercentage, 100)}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">{progress.description}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderProgressCard = (progress: BadgeProgress) => {
    const badge = USER_BADGES[progress.badgeId];
    const percentage = (progress.current / progress.target) * 100;
    const isCompleted = progress.current >= progress.target;

    return (
      <div className={`bg-white rounded-lg border p-4 ${isCompleted ? 'border-green-200' : 'border-gray-200'}`}>
        <div className="flex items-start space-x-3">
          <div className={`w-12 h-12 rounded-full ${badge?.bgColor || 'bg-gray-100'} flex items-center justify-center`}>
            {badge ? (
              <badge.icon className={`w-6 h-6 ${badge.color}`} />
            ) : (
              <Lock className="w-6 h-6 text-gray-400" />
            )}
          </div>
          <div className="flex-1">
            <div className="flex items-center space-x-2">
              <h3 className="font-semibold text-gray-900">{badge?.label || 'Unknown Badge'}</h3>
              {isCompleted && <CheckCircle className="w-4 h-4 text-green-500" />}
            </div>
            <p className="text-sm text-gray-600 mt-1">{progress.description}</p>
            
            <div className="mt-3">
              <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                <span>Progress</span>
                <span>{progress.current}/{progress.target} {progress.unit}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full transition-all duration-300 ${
                    isCompleted ? 'bg-green-500' : 'bg-blue-600'
                  }`}
                  style={{ width: `${Math.min(percentage, 100)}%` }}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {isCompleted ? 'Badge earned!' : `${Math.round(percentage)}% complete`}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-3">
            <AvatarWithBadges user={user} size="md" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Badges & Achievements</h2>
              <p className="text-sm text-gray-600">
                {earnedBadges.length} earned • {availableBadges.length} available
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
              {tab.count > 0 && (
                <span className="ml-2 bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-xs">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {activeTab === 'earned' && (
            <div className="space-y-4">
              {earnedBadges.length > 0 ? (
                earnedBadges.map((badge) => (
                  <div key={badge.id}>
                    {renderBadgeCard(badge, true)}
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <Trophy className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Badges Yet</h3>
                  <p className="text-gray-600">
                    Start using ZippyHealth to earn your first badges!
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'available' && (
            <div className="space-y-4">
              {availableBadges.length > 0 ? (
                availableBadges.map((badge) => (
                  <div key={badge.id}>
                    {renderBadgeCard(badge, false)}
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <Award className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">All Badges Earned!</h3>
                  <p className="text-gray-600">
                    Congratulations! You've earned all available badges.
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'progress' && (
            <div className="space-y-4">
              {badgeProgress.length > 0 ? (
                badgeProgress.map((progress) => (
                  <div key={progress.badgeId}>
                    {renderProgressCard(progress)}
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <Target className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Progress to Track</h3>
                  <p className="text-gray-600">
                    Start using features to track your progress toward badges.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t">
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>Badges help you showcase your achievements and status</span>
            <button
              onClick={() => window.open('/help/badges', '_blank')}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              Learn More
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}; 